<script lang="ts" setup>
import { fromNow } from '@thecompaniesapi/date-helpers'
import { getFrom } from '@thecompaniesapi/object-helpers'
import { segmentation } from '@thecompaniesapi/shared/client'

const props = defineProps({
  action: {
    default: undefined,
    type: Object as PropType<Action>,
  },
  columnsToDisplayInTable: {
    default: undefined,
    type: Array as PropType<UColumn[]>,
  },
  companies: {
    default: () => [],
    type: Array as PropType<CompanyV2[]>,
  },
  companiesPerPage: {
    default: 10,
    type: Number,
  },
  count: {
    default: 0,
    type: Number,
  },
  disableSorting: {
    default: false,
    type: Boolean,
  },
  feature: {
    default: undefined,
    type: String as PropType<SegmentationFeature>,
  },
  freeRequest: {
    default: false,
    type: Boolean,
  },
  freeRequestFill: {
    default: true,
    type: Boolean,
  },
  hasAllSelected: {
    default: undefined,
    type: [Boolean, Array] as PropType<boolean | string[]>,
  },
  isWaiting: {
    default: false,
    type: Boolean,
  },
  lastCompaniesSize: {
    default: 0,
    type: Number,
  },
  loading: {
    default: undefined,
    type: Boolean,
  },
  similarDomains: {
    default: () => [],
    type: Array as PropType<string[]>,
  },
  sortFields: {
    default: undefined,
    type: Object as PropType<SegmentationSortFields>,
  },
  wrapperClasses: {
    default: '',
    type: String,
  },
})

const emit = defineEmits<{
  openProfile: [domain: string | undefined]
  selectAll: []
}>()

const { locale, t } = useI18n()

const selection = defineModel<number[]>({ default: () => [] })

const emptyStatus = computed(() => {
  if (props.feature === 'similar' && !props.similarDomains.length) {
    return 'noDomainsSelected'
  }

  return 'noResults'
})

const attributesNotSimplified = segmentation.attributesWeCanQuery.filter(attribute => !segmentation.attributesSimplified.includes(attribute))

const rows = computed(() => {
  if (props.loading) {
    return Array(props.lastCompaniesSize).fill({}) as (CompanyV2 & { meta?: { freeCompany?: boolean } })[]
  }

  let result: (CompanyV2 & { meta?: { freeCompany?: boolean } })[] = props.companies || []

  if (props.freeRequest && result.length) {
    if (props.freeRequestFill) {
      const fillerCount = Math.max(0, props.companiesPerPage - result.length)

      result = [...result, ...Array(fillerCount).fill({ domain: { domain: 'filler' } })]
    }

    result = result
      .filter(company => !!company?.domain?.domain)
      .map((company) => {
        const { get } = getFrom(company)

        let freeCompany = false

        for (const attribute of attributesNotSimplified) {
          const value = get(attribute as any)

          if (value) {
            freeCompany = true
            break
          }
        }

        return {
          ...company,
          meta: {
            ...(company?.meta || {}),
            freeCompany,
          },
        }
      })
  }

  return result
})
</script>

<template>
  <UTable
    :class="wrapperClasses"
    :columns="columnsToDisplayInTable"
    :loading="loading"
    :rows="rows"
    :ui="{
      wrapper: `ring-1 ring-gray-200 dark:ring-gray-800 rounded-lg shadow-texture relative bg-texture-deep${wrapperClasses ? ` ${wrapperClasses}` : ''}`,
      thead: companies.length ? undefined : '!border-none',
      tbody: '!border-none',
      loadingState: {
        wrapper: 'hidden',
      },
      td: {
        color: 'text-gray-900 dark:text-gray-100 h-24',
      },
      th: {
        base: 'text-xs uppercase p-0 border-b border-gray-200 dark:border-gray-800',
        font: 'font-normal',
      },
      tr: {
        base: 'relative !border-none',
      },
    }"
  >
    <template
      v-for="field in props?.action?.data?.fields"
      :key="field.key"
      #[`${field.key}-header`]="{ column }"
    >
      <CompaniesTableColumnHeader
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template
      v-for="field in props?.action?.data?.fields"
      :key="field.key"
      #[`${field.key}-data`]="{ row }"
    >
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div class="w-full text-balance">
          <div v-if="row?.action?.answer?.output?.[field.key] === 'false' || row?.action?.answer?.output?.[field.key] === false || !row?.action?.answer?.output?.[field.key]">
            <UButton
              color="red"
              :icon="getIcon('notEqual')"
              size="xs"
              variant="subtle"
            >
              {{ t('globals.no') }}
            </UButton>
          </div>

          <div v-else-if="row?.action?.answer?.output?.[field.key] === 'true' || row?.action?.answer?.output?.[field.key] === true">
            <UButton
              color="green"
              :icon="getIcon('check')"
              size="xs"
              variant="subtle"
            >
              {{ t('globals.yes') }}
            </UButton>
          </div>

          <span v-else-if="typeof row?.action?.answer?.output?.[field.key] === 'string'">
            {{ row?.action?.answer?.output?.[field.key] }}
          </span>

          <span v-else-if="typeof row?.action?.answer?.output?.[field.key] === 'number'">
            {{ numbro(row?.action?.answer?.output?.[field.key]) }}
          </span>

          <span v-else-if="Array.isArray(row?.action?.answer?.output?.[field.key])">
            {{ row?.action?.answer?.output?.[field.key].join(', ') }}
          </span>

          <CompaniesTableEmptyString v-else />
        </div>
      </div>
    </template>

    <template #answerScore-header="{ column }">
      <CompaniesTableColumnHeader
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #answerScore-data="{ row }">
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div v-if="row?.action?.answer?.score">
          <CompanyScore
            :score="row?.action?.answer?.score"
            size="xs"
          />
        </div>

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #explanation-header="{ column }">
      <CompaniesTableColumnHeader
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #explanation-data="{ row }">
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div
          v-if="row?.action?.answer?.explanation"
          class="whitespace-pre-wrap"
        >
          {{ row?.action?.answer?.explanation }}
        </div>
      </div>
    </template>

    <template #selection-header>
      <UTooltip
        class="normal-case"
        :class="{ 'pointer-events-none opacity-0': !companies.length }"
        :text="t('selectAll')"
      >
        <UCheckbox
          :model-value="hasAllSelected"
          :ui="{
            base: 'size-4.5 cursor-pointer',
            color: 'text-gray-700 dark:text-gray-700',
          }"
          value="all"
          @click="emit('selectAll')"
        />
      </UTooltip>
    </template>

    <template #selection-data="{ row }">
      <div
        class="flex w-0 items-start"
        :class="{ 'pointer-events-none opacity-0': loading }"
      >
        <UCheckbox
          v-model="selection"
          :ui="{
            base: 'size-4.5 cursor-pointer',
            color: 'text-gray-700 dark:text-gray-700',
          }"
          :value="(row as CompanyV2)?.meta?.mysqlId"
        />
      </div>
    </template>

    <template #logo-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-6 min-w-6 max-w-6"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #logo-data="{ row }">
      <div class="flex w-10 min-w-10 max-w-10 items-start">
        <UTooltip
          :popper="{ placement: 'bottom' }"
          :text="`${t('globals.showProfile')}${row?.domain?.domain ? ` (${row.domain.domain})` : ``}`"
        >
          <UButton
            class="-m-0.5 p-0.5 hover:bg-transparent dark:hover:bg-transparent"
            variant="ghost"
            @click="emit('openProfile', (row as CompanyV2)?.domain?.domain)"
          >
            <ClientOnly>
              <UAvatar
                :alt="(row as CompanyV2)?.about?.name"
                :chip="row?.meta?.new"
                :chip-color="row?.meta?.new ? 'red' : undefined"
                :chip-text="row?.meta?.new ? 'New' : ''"
                :class="{ '!ring-2 !ring-red-500': row?.meta?.new }"
                size="lg"
                :src="getLogo((row as CompanyV2)?.domain?.domain, 'companies')"
                :ui="{
                  chip: {
                    base: '!text-white p-1 ring-1',
                    position: {
                      'top-right': '-top-2 -right-3',
                    },
                  },
                }"
              />
            </ClientOnly>
          </UButton>
        </UTooltip>
      </div>
    </template>

    <template #logos-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-36 min-w-36 max-w-36"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #logos-data="{ row }">
      <div class="flex w-36 min-w-36 max-w-36 items-center gap-2 overflow-y-scroll">
        <UTooltip
          class="flex-shrink-0"
          :popper="{ placement: 'bottom' }"
          :text="`${t('globals.showProfile')}${row?.domain?.domain ? ` (${row.domain.domain})` : ``}`"
        >
          <UButton
            class="p-0.5 hover:bg-transparent dark:hover:bg-transparent"
            variant="ghost"
            @click="emit('openProfile', (row as CompanyV2)?.domain?.domain)"
          >
            <ClientOnly>
              <UAvatar
                :id="(row as CompanyV2)?.domain?.domain"
                :alt="(row as CompanyV2)?.domain?.domain"
                size="lg"
                :src="getLogo((row as CompanyV2)?.domain?.domain, 'companies')"
              />
            </ClientOnly>
          </UButton>
        </UTooltip>

        <UIcon
          class="size-4 flex-shrink-0 text-gray-600 dark:text-gray-400"
          :name="getIcon('similar')"
        />

        <UAvatarGroup
          :max="2"
          size="lg"
        >
          <UTooltip
            v-for="similarDomain in similarDomains"
            :key="`${similarDomain}-${(row as CompanyV2)?.domain?.domain}`"
            class="cursor-pointer !border-transparent !ring-transparent"
            :popper="{ placement: 'bottom' }"
            :text="`${t('globals.showProfile')}${similarDomain ? ` (${similarDomain})` : ``}`"
            @click="emit('openProfile', similarDomain)"
          >
            <ClientOnly>
              <UAvatar
                :alt="similarDomain"
                class="cursor-pointer !border-transparent !ring-transparent"
                size="lg"
                :src="getLogo(similarDomain, 'companies')"
              />
            </ClientOnly>
          </UTooltip>
        </UAvatarGroup>
      </div>
    </template>

    <template #nameAndDomain-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-48 min-w-48 max-w-48"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #nameAndDomain-data="{ row }">
      <div class="flex w-48 min-w-48 max-w-48 flex-col items-start">
        <span class="w-full truncate">
          <template v-if="(row as CompanyV2)?.about?.name">
            {{ (row as CompanyV2)?.about?.name }}
          </template>

          <span
            v-else-if="(row as CompanyV2)?.domain?.domainName"
            class="capitalize"
          >
            {{ (row as CompanyV2)?.domain?.domainName }}
          </span>

          <CompaniesTableEmptyString v-else />
        </span>

        <ULink
          class="text-xs text-indigo-700 underline dark:text-indigo-300"
          target="_blank"
          :to="`http://${(row as CompanyV2)?.domain?.domain}`"
        >
          {{ (row as CompanyV2)?.domain?.domain }}
        </ULink>
      </div>
    </template>

    <template #industries-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-80 min-w-80 max-w-80"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #industries-data="{ row }">
      <div class="flex w-80 min-w-80 max-w-80 items-start overflow-x-auto py-2">
        <CompanyIndustries
          v-if="(row as CompanyV2)?.about?.industries?.length || (row as CompanyV2)?.about?.industry"
          :company="row"
          :free-request="freeRequest && !row?.meta?.freeCompany"
          :max="10"
          size="xs"
          :wrap="false"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #totalEmployees-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-56 min-w-56 max-w-56"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #totalEmployees-data="{ row }">
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div v-if="(row as CompanyV2)?.about?.totalEmployees">
          {{ t(`ranges.totalEmployees.${(row as CompanyV2)?.about?.totalEmployees}`) }}
        </div>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="about.totalEmployees"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #revenue-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-52 min-w-52 max-w-52"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #revenue-data="{ row }">
      <div class="flex w-52 min-w-52 max-w-52 flex-col gap-0.5">
        <div v-if="(row as CompanyV2)?.finances?.revenue">
          {{ t(`ranges.revenue.${(row as CompanyV2)?.finances?.revenue}`) }}
        </div>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="finances.revenue"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #businessType-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-56 min-w-56 max-w-56"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #businessType-data="{ row }">
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div v-if="(row as CompanyV2)?.about?.businessType">
          {{ t(`ranges.businessType.${(row as CompanyV2)?.about?.businessType}`) }}
        </div>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="about.businessType"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #monthlyVisitors-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-52 min-w-52 max-w-52"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #monthlyVisitors-data="{ row }">
      <div
        v-if="(row as CompanyV2)?.analytics?.monthlyVisitors"
        class="flex w-52 min-w-52 max-w-52 items-center"
      >
        <BaseCircle
          class="mr-2 size-4"
          :color="getColorForAttribute('monthlyVisitors', (row as CompanyV2)?.analytics?.monthlyVisitors!)"
          gradient
        />

        {{ t(`ranges.monthlyVisitors.${(row as CompanyV2)?.analytics?.monthlyVisitors}`) }}
      </div>

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="analytics.monthlyVisitors"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #country-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-56 min-w-56 max-w-56"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #country-data="{ row }">
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div
          v-if="(row as CompanyV2)?.locations?.headquarters?.country"
          class="flex items-center"
        >
          <UIcon
            class="size-4.5 mr-2"
            :name="`flagpack:${(row as CompanyV2)?.locations?.headquarters?.country?.code}`"
          />

          {{ t(`locations.countries.${(row as CompanyV2)?.locations?.headquarters?.country?.code}`) }}
        </div>

        <CompaniesTableEmptyString v-else-if="!freeRequest && !row?.meta?.freeCompany" />

        <div
          v-if="(row as CompanyV2)?.locations?.headquarters?.continent"
          class="flex items-center text-gray-700 dark:text-gray-300"
        >
          <UIcon
            class="size-4.5 mr-1.5"
            :name="getIcon('continent')"
          />

          {{ t(`locations.continents.${(row as CompanyV2)?.locations?.headquarters?.continent?.code}`) }}
        </div>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="locations.headquarters.country"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #city-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-56 min-w-56 max-w-56"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #city-data="{ row }">
      <div class="flex w-56 min-w-56 max-w-56 flex-col gap-0.5">
        <div v-if="(row as CompanyV2)?.locations?.headquarters?.city">
          {{ (row as CompanyV2)?.locations?.headquarters?.city?.name }}
        </div>

        <CompaniesTableEmptyString v-else-if="!freeRequest && !row?.meta?.freeCompany" />

        <div
          v-if="(row as CompanyV2)?.locations?.headquarters?.county || (row as CompanyV2)?.locations?.headquarters?.state"
          class="flex items-center overflow-hidden text-gray-700 dark:text-gray-300"
        >
          <UIcon
            class="size-4.5 mr-1"
            :name="getIcon('pin')"
          />

          <span class="truncate">
            {{ `${(row as CompanyV2)?.locations?.headquarters?.county?.name || ''}${(row as CompanyV2)?.locations?.headquarters?.county?.name && (row as CompanyV2)?.locations?.headquarters?.state?.name ? ', ' : ''}${(row as CompanyV2)?.locations?.headquarters?.state?.name || ''}` }}
          </span>
        </div>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="locations.headquarters.city"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #yearFounded-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-48 min-w-48 max-w-48"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #yearFounded-data="{ row }">
      <div
        class="flex w-48 min-w-48 max-w-48 items-center"
        :class="{
          'justify-center': !freeRequest,
        }"
      >
        <span v-if="(row as CompanyV2)?.about?.yearFounded">
          {{ (row as CompanyV2)?.about?.yearFounded }}
        </span>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="about.yearFounded"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #socialNetworks-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-64 min-w-64 max-w-64"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #socialNetworks-data="{ row }">
      <CompanySocials
        v-if="Object.keys((row as CompanyV2)?.socials || {}).length"
        class="w-64 min-w-64 max-w-64"
        has-tooltip
        :socials="(row as CompanyV2)?.socials"
        :urls="(row as CompanyV2)?.urls"
      />

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="socials"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #technologies-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-64 min-w-64 max-w-64"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #apps-data="{ row }">
      <CompanyApps
        v-if="Object.keys((row as CompanyV2)?.apps || {}).length"
        :apps="(row as CompanyV2)?.apps"
        class="w-64 min-w-64 max-w-64"
        has-tooltip
      />

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="apps"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #apps-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-64 min-w-64 max-w-64"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #contacts-data="{ row }">
      <CompanyContacts
        v-if="Object.keys((row as CompanyV2)?.contacts || {}).length"
        class="w-64 min-w-64 max-w-64"
        :contacts="(row as CompanyV2)?.contacts"
        has-tooltip
      />

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="contacts"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #contacts-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-64 min-w-64 max-w-64"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #technologies-data="{ row }">
      <CompanyTechnologies
        v-if="(row as CompanyV2)?.technologies?.active?.length"
        class="w-64 min-w-64 max-w-64"
        :max="7"
        size="xs"
        :technologies="(row as CompanyV2)?.technologies?.active!"
        :technologies-details="(row as CompanyV2)?.technologies?.details"
      />

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="technologies.active"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #lists-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-48 min-w-48 max-w-48"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #lists-data="{ row }">
      <div class="flex w-80 min-w-48 max-w-48 items-start overflow-x-auto py-2">
        <CompanyLists
          v-if="(row as CompanyV2)?.meta?.listIds?.length"
          :list-ids="(row as CompanyV2)?.meta?.listIds"
          :max="10"
          size="xs"
          :wrap="false"
        />

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="meta.listIds"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #similarAttributes-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-200 min-w-200 max-w-200"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #similarAttributes-data="{ row }">
      <div class="w-200 min-w-200 max-w-200 flex items-start overflow-x-auto py-2">
        <CompanySimilarAttributes
          v-if="row?.meta?.similarAttributes?.length"
          :similar-attributes="row?.meta?.similarAttributes"
          size="xs"
          :wrap="false"
        />

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="meta.similarAttributes"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #syncedAt-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-48"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #syncedAt-data="{ row }">
      <div class="flex w-48 items-center px-2">
        <span v-if="(row as CompanyV2)?.meta?.syncedAt">
          {{ fromNow(new Date((row as CompanyV2).meta!.syncedAt!), locale) }}
        </span>

        <CompaniesSimplifiedPlaceholder
          v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
          attribute="meta.syncedAt"
          :company="row"
        />

        <CompaniesTableEmptyString v-else />
      </div>
    </template>

    <template #score-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-48 min-w-48"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #score-data="{ row }">
      <CompanyScore
        v-if="row?.meta?.score"
        class="w-48 min-w-48"
        :score="row?.meta?.score"
      />

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="meta.score"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #similarity-header="{ column }">
      <CompaniesTableColumnHeader
        class="w-48"
        :column="column"
        :disable-sorting="disableSorting"
        :sort-fields="sortFields"
      />
    </template>

    <template #similarity-data="{ row }">
      <CompanySimilarityScore
        v-if="row?.meta?.similarity"
        class="w-48"
        :feature="feature === 'search' ? 'search' : 'similar'"
        :score="row?.meta?.similarity"
      />

      <CompaniesSimplifiedPlaceholder
        v-else-if="freeRequest && !row?.meta?.freeCompany && !loading"
        attribute="meta.similarity"
        :company="row"
      />

      <CompaniesTableEmptyString v-else />
    </template>

    <template #empty-state>
      <div
        v-if="isWaiting"
        class="mx-auto flex h-60 w-full max-w-2xl flex-col items-center justify-center gap-2 sm:h-96"
      >
        <BaseSpinner class="size-12" />

        <BaseMessage
          :description="t('empty.wait.description')"
          :power="false"
          :title="t('empty.wait.title')"
        />
      </div>

      <div
        v-else-if="!loading && !companies.length"
        class="mx-auto flex h-60 w-full max-w-2xl items-center justify-center sm:h-96"
      >
        <BaseMessage
          :description="t(`empty.${feature}.${emptyStatus}.description`)"
          :title="t(`empty.${feature}.${emptyStatus}.title`)"
        />
      </div>
    </template>
  </UTable>
</template>

<i18n lang="json">
{
  "en": {
    "empty": {
      "search": {
        "noResults": {
          "description": "You can contact us if you want us to improve the results for this query.",
          "title": "No companies have been found"
        }
      },
      "similar": {
        "noDomainsSelected": {
          "description": "You must select at least one domain to find similar companies.",
          "title": "No domains have been selected"
        },
        "noResults": {
          "description": "You can contact us if you want us to improve the results for this query.",
          "title": "No similar companies have been found"
        }
      },
      "wait": {
        "description": "The companies will appear in the list when they are ready, you can follow the progress in the Actions tab.",
        "title": "Companies are being added to your list"
      }
    },
    "selectAll": "Select all"
  },
  "es": {
    "empty": {
      "search": {
        "noResults": {
          "description": "Puedes contactarnos si quieres que mejoremos los resultados para esta consulta.",
          "title": "No se encontraron empresas"
        }
      },
      "similar": {
        "noDomainsSelected": {
          "description": "Debes seleccionar al menos un dominio para encontrar empresas similares.",
          "title": "No se han seleccionado dominios"
        },
        "noResults": {
          "description": "Puedes contactarnos si quieres que mejoremos los resultados para esta consulta.",
          "title": "No se encontraron empresas similares"
        }
      },
      "wait": {
        "description": "Las empresas aparecerán en la lista cuando estén listas, puedes seguir el progreso en la pestaña Acciones.",
        "title": "Las empresas están siendo añadidas a tu lista"
      }
    },
    "selectAll": "Seleccionar todo"
  },
  "fr": {
    "empty": {
      "search": {
        "noResults": {
          "description": "Vous pouvez nous contacter si vous souhaitez que nous améliorions les résultats pour cette requête.",
          "title": "Aucune entreprise n'a été trouvée"
        }
      },
      "similar": {
        "noDomainsSelected": {
          "description": "Vous devez sélectionner au moins un domaine pour trouver des entreprises similaires.",
          "title": "Aucun domaine n'a été sélectionné"
        },
        "noResults": {
          "description": "Vous pouvez nous contacter si vous souhaitez que nous améliorions les résultats pour cette requête.",
          "title": "Aucune entreprise similaire n'a été trouvée"
        }
      },
      "wait": {
        "description": "Les entreprises apparaîtront dans la liste lorsqu'elles seront prêtes, vous pouvez suivre leur progression dans l'onglet Actions.",
        "title": "Les entreprises sont en cours d'ajout à votre liste"
      }
    },
    "selectAll": "Tout sélectionner"
  }
}
</i18n>
